// modal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import "./modal.css";
import {
  useSaveColorDataMutation,
  useSaveBrandDataMutation,
  useSaveNumberDataMutation,
  useSaveSpecificTypeDataMutation,
  useSaveLocationsDataMutation,
  useEditColorDataMutation,
  useEditBrandDataMutation,
  useEditNumberDataMutation,
  useEditTypeDataMutation,
  useEditLocationDataMutation,
  useUploadTypeCoverImageMutation,
} from "../api/cmsPanelsApi";
import { useTranslation } from "../hooks/useTranslation";
import { getApiErrorMessage } from "../api/errorUtils";
import {
  hexColorForPicker,
  isValidHexColor,
  normalizeHexColor,
} from "../utils/colorSwatch";

const slugifyProductType = (value = "") =>
  String(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

const AddItemModal = ({
  modalOpen,
  toggleModal,
  itemType,
  addNewItem,
  setTableData,
  editItemData,
  setEditItemData,
  notificationComponentRef,
  tableData = [],
}) => {
  const [saveColorData] = useSaveColorDataMutation();
  const [saveBrandData] = useSaveBrandDataMutation();
  const [saveNumberData] = useSaveNumberDataMutation();
  const [saveLocationsData] = useSaveLocationsDataMutation();
  const [editBrandData] = useEditBrandDataMutation();
  const [editTypeData] = useEditTypeDataMutation();
  const [editColorData] = useEditColorDataMutation();
  const [editNumberData] = useEditNumberDataMutation();
  const [editLocationData] = useEditLocationDataMutation();
  const [saveSpecificTypeData] = useSaveSpecificTypeDataMutation();
  const [uploadTypeCoverImage] = useUploadTypeCoverImageMutation();
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [errors, setErrors] = useState({});
  const t = useTranslation();

  let modalTitle = "";

  // Initialize default values when modal opens
  useEffect(() => {
    if (modalOpen) {
      // Set default type for brands, numbers, and types if not editing
      if (!editItemData.id) {
        const needsType = ["brands", "numbers", "types"].includes(itemType);
        if (needsType && !editItemData.type) {
          // Get the first available type from tableData
          const availableTypes = tableData
            .map((item) => item.type)
            .filter((type) => type && type.trim() !== "")
            .filter((type, index, self) => self.indexOf(type) === index); // Remove duplicates

          // Use the first available type if it exists, otherwise leave empty
          if (availableTypes.length > 0) {
            const defaultValues = {
              ...editItemData,
              type: availableTypes[0],
            };
            setEditItemData(defaultValues);
          }
        }
      }
      setErrors({});
      if (itemType === "colors" && !editItemData.id && !editItemData.hexCode) {
        setEditItemData((current) => ({
          ...current,
          hexCode: "#808080",
        }));
      }
    }
  }, [modalOpen, itemType, tableData]);
  switch (itemType) {
    case "brands":
      modalTitle = "Add New Brand";
      break;
    case "colors":
      modalTitle = "Add New Color";
      break;
    case "numbers":
      modalTitle = "Add New Number";
      break;
    case "types":
      modalTitle = "Add New Type";
      break;
    case "locations":
      modalTitle = "Add New Location";
      break;
    default:
      modalTitle = "";
  }
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue =
      type === "checkbox"
        ? checked
        : type === "number"
        ? value === ""
          ? ""
          : Number(value)
        : value;
    const updatedData = { ...editItemData, [name]: fieldValue };

    if (itemType === "types" && name === "specificType" && !editItemData.id) {
      updatedData.slug = slugifyProductType(value);
    }

    setEditItemData(updatedData);
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleTypeSlugBlur = () => {
    if (itemType !== "types") return;

    setEditItemData((current) => ({
      ...current,
      slug: slugifyProductType(current.slug || current.specificType || ""),
    }));
  };

  const handleTypeCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      setUploadingCover(true);
      const response = await uploadTypeCoverImage(file).unwrap();
      const media = response?.data || {};

      setEditItemData((current) => ({
        ...current,
        coverImageKey: media.coverImageKey || null,
        coverImageThumbnailKey: media.coverImageThumbnailKey || null,
        coverImageCardKey: media.coverImageCardKey || null,
        coverImageFullKey: media.coverImageFullKey || null,
        coverImageUrl: media.coverImageUrl || null,
      }));
    } catch (error) {
      console.error("Error uploading cover image:", error);
      notificationComponentRef?.current?.showNotification(
        getApiErrorMessage(
          error,
          t("catalogProductStudio.errorMediaUpload") || "Unable to upload image."
        ),
        "danger"
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveTypeCover = () => {
    setEditItemData((current) => ({
      ...current,
      coverImageKey: null,
      coverImageThumbnailKey: null,
      coverImageCardKey: null,
      coverImageFullKey: null,
      coverImageUrl: null,
    }));
  };

  const handleHexColorPickerChange = (e) => {
    const normalized = normalizeHexColor(e.target.value);
    setEditItemData((current) => ({
      ...current,
      hexCode: normalized || current.hexCode || "#808080",
    }));
    if (errors.hexCode) {
      setErrors((current) => ({ ...current, hexCode: null }));
    }
  };

  const handleHexInputChange = (e) => {
    const { value } = e.target;
    setEditItemData((current) => ({
      ...current,
      hexCode: value,
    }));
    if (errors.hexCode) {
      setErrors((current) => ({ ...current, hexCode: null }));
    }
  };

  const handleHexInputBlur = () => {
    const normalized = normalizeHexColor(editItemData.hexCode);
    if (normalized) {
      setEditItemData((current) => ({
        ...current,
        hexCode: normalized,
      }));
    }
  };

  const buildReferenceSubmitPayload = (type, data) => {
    const isEdit = Boolean(data.id);

    switch (type) {
      case "colors": {
        const payload = {
          colorName: data.colorName,
          albanianName: data.albanianName,
          englishName: data.englishName,
          turkishName: data.turkishName,
        };
        const normalizedHex = normalizeHexColor(data.hexCode);
        if (normalizedHex) {
          payload.hexCode = normalizedHex;
        } else if (!isEdit) {
          payload.hexCode = null;
        }
        if (isEdit) {
          payload.id = data.id;
        }
        return payload;
      }
      case "brands": {
        const payload = {
          brandName: data.brandName,
          produced: data.produced,
          type: data.type,
        };
        if (isEdit) {
          payload.id = data.id;
        }
        return payload;
      }
      case "numbers": {
        const payload = {
          number: data.number,
          type: data.type,
        };
        if (isEdit) {
          payload.id = data.id;
        }
        return payload;
      }
      case "types": {
        const payload = {
          specificType: data.specificType,
          type: data.type,
          slug: slugifyProductType(data.slug || data.specificType),
          coverImageKey: data.coverImageKey ?? null,
          coverImageThumbnailKey: data.coverImageThumbnailKey ?? null,
          coverImageCardKey: data.coverImageCardKey ?? null,
          coverImageFullKey: data.coverImageFullKey ?? null,
          isPublished: Boolean(data.isPublished ?? data.publishedInStorefront),
          sortOrder: Number(data.sortOrder || 0),
        };
        if (isEdit) {
          payload.id = data.id;
        }
        return payload;
      }
      case "locations": {
        const payload = { name: data.name };
        if (isEdit) {
          payload.id = data.id;
        }
        return payload;
      }
      default:
        return { ...data };
    }
  };

  const updateItemProperty = (formData, tableData) => {
    const updatedItems = tableData.map((item) => {
      if (item.id === formData.id) {
        const updatedItem = { ...item }; // Create a new object to hold the updated values
        const keys = Object.keys(formData);

        for (let j = 0; j < keys.length; j++) {
          const element = keys[j];
          updatedItem[element] = formData[element];
        }

        return updatedItem; // Return the updated item
      }

      return item;
    });

    setTableData(updatedItems);
    toggleModal();
    notificationComponentRef.current.showNotification(
      `Item of ${itemType} with ID:${formData.id} has updated successfully`,
      "success"
    );
  };

  const validateForm = () => {
    const newErrors = {};
    let requiredFields = [];

    switch (itemType) {
      case "brands":
        requiredFields = ["type", "brandName", "produced"];
        break;
      case "colors":
        requiredFields = editItemData.id
          ? ["colorName", "albanianName", "englishName", "turkishName"]
          : ["colorName", "hexCode", "albanianName", "englishName", "turkishName"];
        break;
      case "numbers":
        requiredFields = ["type", "number"];
        break;
      case "types":
        requiredFields = ["type", "specificType", "slug"];
        break;
      case "locations":
        requiredFields = ["name"];
        break;
      default:
        return false;
    }

    requiredFields.forEach((key) => {
      const value = editItemData[key];
      if (!value || (typeof value === "string" && !value.trim())) {
        // Get translated field name from table translations
        const fieldName =
          t(`table.${key}`) ||
          t(`cms.${key}`) ||
          key.charAt(0).toUpperCase() + key.slice(1);
        const errorMsg =
          t("cms.fieldRequired")?.replace("{field}", fieldName) ||
          `${fieldName} is required!`;
        newErrors[key] = errorMsg;
      }
    });

    if (itemType === "colors" && editItemData.hexCode && !isValidHexColor(editItemData.hexCode)) {
      newErrors.hexCode =
        t("cms.invalidHexColor") || "HEX code must be in #RRGGBB format";
    }

    if (
      itemType === "types" &&
      editItemData.slug &&
      !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(editItemData.slug)
    ) {
      newErrors.slug =
        t("cms.invalidSlug") ||
        "Slug can use lowercase letters, numbers, and hyphens only.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      if (notificationComponentRef?.current) {
        notificationComponentRef.current.showNotification(
          t("cms.validationError") ||
            "Please fill in all required fields correctly",
          "danger"
        );
      }
      return;
    }

    try {
      let data;
      setLoading(true);

      // Send only fields allowed by the backend DTOs (ValidationPipe rejects extras like `name`).
      const submitData = buildReferenceSubmitPayload(itemType, editItemData);

      if (!editItemData.id) {
        // Adding new item
        switch (itemType) {
          case "brands":
            data = await saveBrandData(submitData).unwrap();
            break;
          case "colors":
            data = await saveColorData(submitData).unwrap();
            break;
          case "numbers":
            data = await saveNumberData(submitData).unwrap();
            break;
          case "types":
            data = await saveSpecificTypeData(submitData).unwrap();
            break;
          case "locations":
            data = await saveLocationsData(submitData).unwrap();
            break;
          default:
            break;
        }

        const newObj = {
          id: data?.data || data?.id,
          ...editItemData,
          ...submitData,
        };
        addNewItem(newObj);

        if (notificationComponentRef?.current) {
          notificationComponentRef.current.showNotification(
            `${t("cms.itemAdded")} ${itemType} ${t("cms.successfully")}`,
            "success"
          );
        }
        toggleModal();
      } else {
        // Updating existing item
        switch (itemType) {
          case "brands":
            await editBrandData(submitData).unwrap();
            break;
          case "colors":
            await editColorData(submitData).unwrap();
            break;
          case "numbers":
            await editNumberData(submitData).unwrap();
            break;
          case "types":
            await editTypeData(submitData).unwrap();
            break;
          case "locations":
            await editLocationData(submitData).unwrap();
            break;
          default:
            break;
        }
        updateItemProperty({ ...editItemData, ...submitData }, tableData);
      }
    } catch (error) {
      console.error("Error saving data:", error);
      if (notificationComponentRef?.current) {
        notificationComponentRef.current.showNotification(
          getApiErrorMessage(error, t("cms.errorSaving") || "Error saving data", {
            403: "Only company managers, admins, or owners can manage reference data.",
            404: "That reference item no longer exists in your company scope.",
            409: "This reference-data change conflicts with existing records.",
          }),
          "danger"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const focusFirstField = () => {
    requestAnimationFrame(() => {
      const root = document.getElementById("cms-add-item-modal");
      const el = root?.querySelector(
        "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])"
      );
      el?.focus?.();
    });
  };

  return (
    <Modal
      id="cms-add-item-modal"
      className="add-item-modal"
      isOpen={modalOpen}
      toggle={toggleModal}
      onOpened={focusFirstField}
    >
      <ModalHeader toggle={toggleModal}>{modalTitle}</ModalHeader>
      <ModalBody>
        <Form>
          {itemType === "brands" && (
            <>
              <FormGroup>
                <Label for="type">{t("table.type") || "Type"} *</Label>
                <Input
                  type="select"
                  name="type"
                  id="type"
                  value={editItemData.type || "shoes"}
                  onChange={handleInputChange}
                  invalid={!!errors.type}
                >
                  <option value="shoes">
                    {t("dashboard.shoes") || "Shoes"}
                  </option>
                  <option value="textile">
                    {t("dashboard.textile") || "Textile"}
                  </option>
                  <option value="accessories">
                    {t("addProducts.accessories") || "Accessories"}
                  </option>
                </Input>
                {errors.type && (
                  <div className="invalid-feedback d-block">{errors.type}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="brandName">
                  {t("table.brand") || "Brand Name"} *
                </Label>
                <Input
                  type="text"
                  name="brandName"
                  id="brandName"
                  value={editItemData.brandName || ""}
                  placeholder={t("cms.enterBrandName") || "Enter Brand Name"}
                  onChange={handleInputChange}
                  invalid={!!errors.brandName}
                />
                {errors.brandName && (
                  <div className="invalid-feedback d-block">
                    {errors.brandName}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="stateName">
                  {t("table.produced") || "Manufacturing Country"} *
                </Label>
                <Input
                  type="text"
                  name="produced"
                  id="stateName"
                  value={editItemData.produced || ""}
                  placeholder={
                    t("cms.enterManufacturingCountry") ||
                    "Enter Manufacturing Country name"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.produced}
                />
                {errors.produced && (
                  <div className="invalid-feedback d-block">
                    {errors.produced}
                  </div>
                )}
              </FormGroup>
            </>
          )}
          {itemType === "colors" && (
            <>
              <FormGroup>
                <Label for="colorName">
                  {t("table.colorName") || "Color Name"} *
                </Label>
                <Input
                  type="text"
                  name="colorName"
                  id="colorName"
                  value={editItemData.colorName || ""}
                  placeholder={
                    t("cms.enterBasicColorName") || "Enter basic color name"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.colorName}
                />
                {errors.colorName && (
                  <div className="invalid-feedback d-block">
                    {errors.colorName}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="hexCode">
                  {t("table.hexCode") || "Color (HEX)"} *
                </Label>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    type="color"
                    name="hexCodePicker"
                    id="hexCodePicker"
                    value={hexColorForPicker(editItemData.hexCode)}
                    onChange={handleHexColorPickerChange}
                    style={{ width: "56px", height: "38px", padding: "2px" }}
                  />
                  <Input
                    type="text"
                    name="hexCode"
                    id="hexCode"
                    value={editItemData.hexCode || ""}
                    placeholder={t("cms.enterHexColor") || "Enter HEX code (#RRGGBB)"}
                    onChange={handleHexInputChange}
                    onBlur={handleHexInputBlur}
                    invalid={!!errors.hexCode}
                  />
                </div>
                {errors.hexCode && (
                  <div className="invalid-feedback d-block">
                    {errors.hexCode}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="albanian">
                  {t("table.albanian") || "Albanian Name"} *
                </Label>
                <Input
                  type="text"
                  name="albanianName"
                  id="albanianName"
                  value={editItemData.albanianName || ""}
                  placeholder={
                    t("cms.enterAlbanianColorName") ||
                    "Enter albanian color name"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.albanianName}
                />
                {errors.albanianName && (
                  <div className="invalid-feedback d-block">
                    {errors.albanianName}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="englishName">
                  {t("table.english") || "English Name"} *
                </Label>
                <Input
                  type="text"
                  name="englishName"
                  id="englishName"
                  value={editItemData.englishName || ""}
                  placeholder={
                    t("cms.enterEnglishColorName") || "Enter english color name"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.englishName}
                />
                {errors.englishName && (
                  <div className="invalid-feedback d-block">
                    {errors.englishName}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="turkishName">
                  {t("table.turkish") || "Turkish Name"} *
                </Label>
                <Input
                  type="text"
                  name="turkishName"
                  id="turkishName"
                  value={editItemData.turkishName || ""}
                  placeholder={
                    t("cms.enterTurkishName") || "Enter turkish name"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.turkishName}
                />
                {errors.turkishName && (
                  <div className="invalid-feedback d-block">
                    {errors.turkishName}
                  </div>
                )}
              </FormGroup>
            </>
          )}
          {itemType === "numbers" && (
            <>
              <FormGroup>
                <Label for="type">{t("table.type") || "Type"} *</Label>
                <Input
                  type="select"
                  name="type"
                  id="type"
                  value={editItemData.type || "shoes"}
                  onChange={handleInputChange}
                  invalid={!!errors.type}
                >
                  <option value="shoes">
                    {t("dashboard.shoes") || "Shoes"}
                  </option>
                  <option value="textile">
                    {t("dashboard.textile") || "Textile"}
                  </option>
                  <option value="accessories">
                    {t("addProducts.accessories") || "Accessories"}
                  </option>
                </Input>
                {errors.type && (
                  <div className="invalid-feedback d-block">{errors.type}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="number">{t("table.number") || "Number"} *</Label>
                <Input
                  type="text"
                  name="number"
                  id="number"
                  value={editItemData.number || ""}
                  placeholder={t("cms.enterNumber") || "Enter Number"}
                  onChange={handleInputChange}
                  invalid={!!errors.number}
                />
                {errors.number && (
                  <div className="invalid-feedback d-block">
                    {errors.number}
                  </div>
                )}
              </FormGroup>
            </>
          )}
          {itemType === "types" && (
            <>
              <FormGroup>
                <Label for="type">{t("table.type") || "Type"} *</Label>
                <Input
                  type="select"
                  name="type"
                  id="type"
                  value={editItemData.type || "shoes"}
                  onChange={handleInputChange}
                  invalid={!!errors.type}
                >
                  <option value="shoes">
                    {t("dashboard.shoes") || "Shoes"}
                  </option>
                  <option value="textile">
                    {t("dashboard.textile") || "Textile"}
                  </option>
                </Input>
                {errors.type && (
                  <div className="invalid-feedback d-block">{errors.type}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="specificType">
                  {t("table.specificType") || "Specific Type"} *
                </Label>
                <Input
                  type="text"
                  name="specificType"
                  id="specificType"
                  value={editItemData.specificType || ""}
                  placeholder={
                    t("cms.enterSpecificType") || "Enter Specific Type"
                  }
                  onChange={handleInputChange}
                  invalid={!!errors.specificType}
                />
                {errors.specificType && (
                  <div className="invalid-feedback d-block">
                    {errors.specificType}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="slug">Slug *</Label>
                <Input
                  type="text"
                  name="slug"
                  id="slug"
                  value={editItemData.slug || ""}
                  placeholder="sandals"
                  onChange={handleInputChange}
                  onBlur={handleTypeSlugBlur}
                  invalid={!!errors.slug}
                />
                {errors.slug && (
                  <div className="invalid-feedback d-block">
                    {errors.slug}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="sortOrder">Sort order</Label>
                <Input
                  type="number"
                  min="0"
                  name="sortOrder"
                  id="sortOrder"
                  value={editItemData.sortOrder ?? 0}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="checkbox"
                    name="isPublished"
                    checked={Boolean(
                      editItemData.isPublished ??
                        editItemData.publishedInStorefront
                    )}
                    onChange={handleInputChange}
                  />{" "}
                  Publish in storefront
                </Label>
              </FormGroup>
              <FormGroup>
                <Label for="coverImage">Storefront cover image</Label>
                <div className="cms-type-cover-editor">
                  <div className="cms-type-cover-preview">
                    {editItemData.coverImageUrl ? (
                      <img
                        src={editItemData.coverImageUrl}
                        alt={editItemData.specificType || "Category cover"}
                      />
                    ) : (
                      <span>No cover</span>
                    )}
                  </div>
                  <div className="cms-type-cover-actions">
                    <Input
                      type="file"
                      id="coverImage"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleTypeCoverUpload}
                      disabled={uploadingCover}
                    />
                    {editItemData.coverImageKey && (
                      <Button
                        type="button"
                        size="sm"
                        color="secondary"
                        onClick={handleRemoveTypeCover}
                        disabled={uploadingCover}
                      >
                        Remove cover
                      </Button>
                    )}
                    {uploadingCover && (
                      <small className="text-muted d-block">
                        Uploading image...
                      </small>
                    )}
                  </div>
                </div>
              </FormGroup>
            </>
          )}
          {itemType === "locations" && (
            <>
              <FormGroup>
                <Label for="name">{t("table.locations") || "Name"} *</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={editItemData.name || ""}
                  placeholder={t("cms.enterName") || "Enter Name"}
                  onChange={handleInputChange}
                  invalid={!!errors.name}
                />
                {errors.name && (
                  <div className="invalid-feedback d-block">{errors.name}</div>
                )}
              </FormGroup>
            </>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading
            ? t("common.saving") || "Saving..."
            : t("common.save") || "Save"}
        </Button>
        <Button color="secondary" onClick={toggleModal} disabled={loading}>
          {t("common.cancel") || "Cancel"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddItemModal;
